/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import * as Sentry from '@sentry/react';
import { memo, useCallback, useEffect, useId, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';
import { formatTime, getStatusColor } from '../common/util/formatter';
import { checkClusters } from '../common/util/geospatial';
import {
  useAttributePreference,
  usePreference,
} from '../common/util/preferences';
import { findFonts } from './core/mapUtil';
import { map } from './core/MapView';
import { mapIconKey, mapImages } from './core/preloadImages';

// Convierte el ImageData de un ícono ya generado a un data URL PNG, para
// poder verlo de verdad (pegando la URL en una pestaña nueva) en vez de
// solo saber que "existe" el objeto. Útil para confirmar si el ícono
// generado está corrupto/en blanco en algún hardware específico, algo
// que no se puede reproducir simulando solo condiciones de red.
const imageDataToDataUrl = (imageData) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
};

const propPrint = (prop) => {
  switch (typeof prop) {
    case 'object':
      return JSON.stringify(prop);
    case 'undefined':
      return 'NULL';
    default:
      return prop;
  }
};

const MapPositions = ({
  positions,
  onClick,
  titleField,
  selectedPosition,
  showStatus = true,
  index,
}) => {
  const id = useId();
  window.id = id;
  window.map = map;

  const icons = `icons-${id}`;
  const backgrounds = `backgrounds--${id}`;
  const clusters = `clusters-${id}`;
  const directions = `directions-${id}`;

  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('sm'));
  const iconScale = useAttributePreference('iconScale', desktop ? 0.75 : 1);

  const devices = useSelector((state) => state.devices.items);
  window.devices = useSelector((state) => state.devices.items);

  const mapCluster = useAttributePreference('mapCluster', false);
  const hours12 = usePreference('twelveHourFormat');
  const directionType = useAttributePreference('mapDirection', 'selected');

  const createFeature = (devices, position) => {
    const device = (devices || window.devices || {})[position?.deviceId];
    const category = mapIconKey(device?.category);
    const color = showStatus
      ? position?.attributes.color || getStatusColor(device?.status)
      : 'neutral';

    return {
      id: position?.id,
      deviceId: position?.deviceId,
      name: device?.name || '',
      fixTime: formatTime(position?.fixTime, 'seconds', hours12),
      category,
      color,
      rotation: position?.course,
      direction: true,
      rotate: device?.category === 'carDirection',
      background: device?.attributes?.background || '#163b61',
    };
  };

  const dataGenerator = (visiblePositions, devices) => ({
    type: 'FeatureCollection',
    features: visiblePositions.map((position) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [position.longitude, position.latitude],
      },
      properties: createFeature(
        devices,
        position,
        selectedPosition && selectedPosition.id,
      ),
    })),
  });

  const onMouseEnter = () => (map.getCanvas().style.cursor = 'pointer');
  const onMouseLeave = () => (map.getCanvas().style.cursor = '');

  const onMarkerClick = useCallback(
    (event) => {
      event.preventDefault();
      const feature = event.features[0];
      if (onClick) {
        onClick(feature.properties.id, feature.properties.deviceId, map, event);
      }
    },
    [onClick],
  );

  const onMapClick = useCallback(
    (event) => {
      if (!event.defaultPrevented && onClick) {
        onClick();
      }
    },
    [onClick],
  );

  const onClusterClick = useCallback(
    (event) => {
      event.preventDefault();
      const features = map.queryRenderedFeatures(event.point, {
        layers: [clusters],
      });
      const clusterId = features[0].properties.cluster_id;
      map.getSource(id).getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (!error && features[0] && features[0].geometry) {
          map.easeTo({
            center: features[0]?.geometry?.coordinates,
            zoom,
          });
        }
      });
    },
    [clusters],
  );

  useEffect(() => {
    if (map.getSource(id)) return () => { };

    let cancelled = false;
    let layersCreated = false;

    // -----------------------------------------------------------------
    // Confirmado con logs reales: bajo conexión lenta, este efecto podía
    // correr con map.isStyleLoaded() === false — el estilo del mapa
    // (sprites/sources) todavía no había terminado de cargar cuando se
    // intentaban agregar las capas de íconos. En conexión rápida esto
    // casi nunca se nota porque el estilo carga casi instantáneo, pero
    // en mobile/redes lentas es mucho más probable, y puede dejar las
    // capas en un estado inconsistente sin ningún error visible.
    //
    // Ahora esperamos explícitamente a que el estilo esté listo antes
    // de crear fuente y capas.
    // -----------------------------------------------------------------
    const createLayers = () => {
      if (cancelled || map.getSource(id)) return;
      layersCreated = true;

      console.info('[MapPositions] creando capas — variables de layout:', {
        iconScale,
        typeofIconScale: typeof iconScale,
        desktop,
        hours12,
        directionType,
        mapCluster,
        titleField,
        showStatus,
        mapHasStyle: !!map.getStyle(),
        mapIsStyleLoaded: map.isStyleLoaded?.(),
      });

      map.addSource(id, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        cluster: mapCluster,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      map.addLayer({
        id: backgrounds,
        type: 'symbol',
        source: id,
        filter: ['!has', 'point_count'],
        layout: {
          'icon-image': 'background',
          'icon-size': iconScale,
          'icon-allow-overlap': true,
        },
        paint: {
          'icon-color': ['get', 'background'],
        },
      });

      map.addLayer({
        id: icons,
        type: 'symbol',
        source: id,
        filter: ['!has', 'point_count'],
        glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
        layout: {
          'icon-image': '{category}-{color}',
          'icon-size': iconScale / 2,
          'icon-allow-overlap': true,
          'text-field': titleField ? `{${titleField}}` : '',
          'text-allow-overlap': true,
          'text-anchor': 'bottom',
          'text-offset': [0, -2 * iconScale],
          'text-font': findFonts(map),
          'text-size': 14,
          'icon-rotate': [
            'case',
            ['==', ['get', 'rotate'], true],
            ['get', 'rotation'],
            0,
          ],
          'icon-rotation-alignment': 'viewport',
        },
        paint: {
          'text-halo-color': 'white',
          'text-halo-width': 2,
          'text-halo-blur': 0,
          'text-color': '#000000',
        },
      });

      map.addLayer({
        id: directions,
        type: 'symbol',
        source: id,
        filter: ['all', ['!has', 'point_count'], ['==', 'direction', true]],
        layout: {
          'icon-image': 'direction',
          'icon-size': iconScale,
          'icon-allow-overlap': true,
          'icon-rotate': ['get', 'rotation'],
          'icon-rotation-alignment': 'map',
        },
      });

      map.addLayer({
        id: clusters,
        type: 'symbol',
        source: id,
        filter: ['has', 'point_count'],
        glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
        layout: {
          'icon-image': 'cluster',
          'icon-size': iconScale,
          'text-field': '{point_count_abbreviated}',
          'text-font': findFonts(map),
          'text-size': 14,
        },
      });

      map.on('mouseenter', icons, onMouseEnter);
      map.on('mouseleave', icons, onMouseLeave);
      map.on('mouseenter', clusters, onMouseEnter);
      map.on('mouseleave', clusters, onMouseLeave);
      map.on('click', icons, onMarkerClick);
      map.on('click', clusters, onClusterClick);
      map.on('click', onMapClick);

      window.createFeature = createFeature;
      window.dataGenerator = dataGenerator;
      window.isEqual = isEqual;
      window.propPrint = propPrint;
    };

    if (map.isStyleLoaded?.()) {
      createLayers();
    } else {
      console.warn('[MapPositions] estilo del mapa no listo aún, esperando evento "idle" antes de crear capas');
      map.once('idle', createLayers);
    }

    return () => {
      cancelled = true;
      map.off('idle', createLayers);

      if (!layersCreated) return;

      window.createFeature = null;
      window.devices = null;
      window.isEqual = null;
      window.propPrint = null;
      map.off('mouseenter', id, onMouseEnter);
      map.off('mouseleave', id, onMouseLeave);
      map.off('mouseenter', clusters, onMouseEnter);
      map.off('mouseleave', clusters, onMouseLeave);
      map.off('click', id, onMarkerClick);
      map.off('click', clusters, onClusterClick);
      map.off('click', onMapClick);

      [icons, backgrounds, clusters, directions, id].forEach((layer) => {
        if (map.getLayer(layer)) map.removeLayer(layer);
        if (map.getSource(layer)) map.removeSource(layer);
      });

      Object.keys(window.players ?? {}).forEach((id) =>
        window.players[id].reset('unmount'),
      );
    };
  }, []);

  // -----------------------------------------------------------------
  // Antes, 'icon-size' se fijaba UNA sola vez al crear la capa (efecto
  // con deps []), usando el valor de iconScale disponible en ese
  // instante. Si useAttributePreference('iconScale', ...) resuelve su
  // valor real de forma asíncrona (después del primer render), la capa
  // podía quedar creada con un tamaño 0/NaN "horneado" para siempre —
  // el ícono existe y está bien registrado, pero se dibuja invisible,
  // sin ningún error, sin autorepararse nunca.
  //
  // Ahora, cada vez que iconScale cambia (incluida la primera vez que
  // termina de resolverse su valor real), forzamos el tamaño correcto
  // sobre la capa ya existente.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!map.getLayer(icons)) return;
    console.info('[MapPositions] actualizando icon-size — iconScale:', iconScale, 'valor final icon-size:', iconScale / 2);
    map.setLayoutProperty(icons, 'icon-size', iconScale / 2);
    if (map.getLayer(backgrounds)) {
      map.setLayoutProperty(backgrounds, 'icon-size', iconScale);
    }
    if (map.getLayer(directions)) {
      map.setLayoutProperty(directions, 'icon-size', iconScale);
    }
    if (map.getLayer(clusters)) {
      map.setLayoutProperty(clusters, 'icon-size', iconScale);
    }
  }, [iconScale]);

  // -----------------------------------------------------------------
  // El diagnóstico de íconos (existencia + dataURL para inspección
  // visual) va en su PROPIO efecto, separado del render de posiciones.
  // Antes estaba dentro del efecto que depende de `positions`, que se
  // actualiza cada 1s (por la cola de posiciones del WebSocket en
  // SocketController) — regenerar y loguear un dataURL en base64 por
  // cada ícono, cada segundo, es costoso (canvas + toDataURL) y además
  // clava la consola con strings enormes. Acá solo depende de
  // `devices`, que cambia con mucha menos frecuencia, y deduplica por
  // combinación única de ícono en vez de repetir por cada posición.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!devices || Object.keys(devices).length === 0) return;

    const seenKeys = new Set();
    const iconReport = [];

    Object.values(devices).forEach((device) => {
      const category = mapIconKey(device?.category);
      // Sin una posición real no sabemos el color exacto (puede venir
      // de position.attributes.color), así que revisamos con el color
      // derivado del status del device como aproximación razonable —
      // suficiente para detectar íconos faltantes/corruptos por
      // categoría, sin tener que repetir por cada posición.
      const color = getStatusColor(device?.status);
      const key = `${category}-${color}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);

      const exists = map.hasImage(key);
      const rawImageData = mapImages[key];
      const hasValidDimensions = rawImageData?.width > 0 && rawImageData?.height > 0;

      iconReport.push({
        deviceName: device?.name,
        deviceCategory: device?.category,
        resolvedKey: key,
        existsInMap: exists,
        imageWidth: rawImageData?.width,
        imageHeight: rawImageData?.height,
      });

      if (!exists) {
        console.warn(`[MapPositions] ícono "${key}" NO EXISTE en el mapa (ej. dispositivo: ${device?.name})`);
      } else if (!hasValidDimensions) {
        console.error(`[MapPositions] ícono "${key}" existe pero con dimensiones inválidas (${rawImageData?.width}x${rawImageData?.height}) — posible ImageData corrupto`);
        const dataUrl = imageDataToDataUrl(rawImageData);
        console.info(`[MapPositions] data URL del ícono sospechoso "${key}" (pegar en pestaña nueva para verlo):`, dataUrl);
      }
    });

    const missing = iconReport.filter((r) => !r.existsInMap);
    if (missing.length > 0) {
      console.warn('[MapPositions] resumen de íconos faltantes:', missing);
      Sentry.captureMessage('Marcador sin ícono registrado en el mapa', {
        level: 'error',
        extra: {
          missing,
          totalChecked: iconReport.length,
          devicePixelRatio: window.devicePixelRatio,
          userAgent: navigator.userAgent,
        },
      });
    }
  }, [devices]);

  useEffect(() => {
    if (!positions || positions.length <= 0) return;

    const source = map.getSource(id);
    if (!source) return;

    const visiblePositions = positions.filter((it) => devices.hasOwnProperty(it.deviceId));

    source.setData({
      type: 'FeatureCollection',
      features: visiblePositions
        .map((position) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [position.longitude, position.latitude],
          },
          properties: createFeature(
            devices,
            position,
            selectedPosition && selectedPosition.id,
          ),
        })),
    });
    checkClusters(positions);
  }, [mapCluster, clusters, devices, positions, selectedPosition, index]);

  return true;
};

export default memo(MapPositions);